import { useEffect, useRef, useState, useCallback } from 'react'

interface FloatingCartButtonProps {
  count: number
  onClick: () => void
}

const DRAG_THRESHOLD = 8
const EDGE_PADDING = 16
const INITIAL_RIGHT = 24
const INITIAL_BOTTOM = 100

export function FloatingCartButton({ count, onClick }: FloatingCartButtonProps) {
  const [position, setPosition] = useState({ right: INITIAL_RIGHT, bottom: INITIAL_BOTTOM })
  const [isDragging, setIsDragging] = useState(false)
  const [hasMoved, setHasMoved] = useState(false)
  const dragStartRef = useRef<{ x: number; y: number } | null>(null)
  const positionStartRef = useRef<{ right: number; bottom: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const getViewportBounds = useCallback(() => {
    const btn = buttonRef.current
    if (!btn) return { maxRight: 0, maxBottom: 0, minRight: 0, minBottom: 0 }
    const rect = btn.getBoundingClientRect()
    const btnWidth = rect.width
    const btnHeight = rect.height
    return {
      maxRight: window.innerWidth - EDGE_PADDING,
      maxBottom: window.innerHeight - EDGE_PADDING,
      minRight: EDGE_PADDING,
      minBottom: EDGE_PADDING,
      btnWidth,
      btnHeight,
    }
  }, [])

  const clampPosition = useCallback((right: number, bottom: number) => {
    const bounds = getViewportBounds()
    return {
      right: Math.max(bounds.minRight, Math.min(bounds.maxRight, right)),
      bottom: Math.max(bounds.minBottom, Math.min(bounds.maxBottom, bottom)),
    }
  }, [getViewportBounds])

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const clientX = e.clientX
    const clientY = e.clientY

    dragStartRef.current = { x: clientX, y: clientY }
    positionStartRef.current = { ...position }
    setHasMoved(false)
    setIsDragging(true)

    if (buttonRef.current) {
      buttonRef.current.setPointerCapture(e.pointerId)
    }
  }, [position])

  const handlePointerMove = useCallback((e: PointerEvent) => {
    if (!isDragging || !dragStartRef.current || !positionStartRef.current) return

    const clientX = e.clientX
    const clientY = e.clientY

    const deltaX = clientX - dragStartRef.current.x
    const deltaY = clientY - dragStartRef.current.y
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)

    if (distance > DRAG_THRESHOLD) {
      setHasMoved(true)
    }

    const newRight = positionStartRef.current.right - deltaX
    const newBottom = positionStartRef.current.bottom + deltaY
    const clamped = clampPosition(newRight, newBottom)

    setPosition(clamped)
  }, [isDragging, clampPosition])

  const handlePointerUp = useCallback((e: PointerEvent) => {
    if (!isDragging) return

    const wasDragging = hasMoved
    setIsDragging(false)
    setHasMoved(false)
    dragStartRef.current = null
    positionStartRef.current = null

    if (buttonRef.current) {
      try {
        buttonRef.current.releasePointerCapture(e.pointerId)
      } catch {
        // ignore pointer capture release errors
      }
    }

    if (!wasDragging) {
      onClick()
    }
  }, [isDragging, hasMoved, onClick])

  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (hasMoved) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    onClick()
  }, [hasMoved, onClick])

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('pointermove', handlePointerMove)
      window.addEventListener('pointerup', handlePointerUp)
      window.addEventListener('pointercancel', handlePointerUp)
    }
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerUp)
    }
  }, [isDragging, handlePointerMove, handlePointerUp])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const clamped = clampPosition(position.right, position.bottom)
      if (clamped.right !== position.right || clamped.bottom !== position.bottom) {
        setPosition(clamped)
      }
    }
  }, [clampPosition, position.right, position.bottom])

  return (
    <button
      ref={buttonRef}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
      className={`floating-cart-btn ${isDragging ? 'dragging' : ''} ${hasMoved ? 'moved' : ''}`}
      aria-label={count > 0 ? `Keranjang belanja, ${count} item` : 'Keranjang belanja kosong'}
      style={{ right: position.right, bottom: position.bottom }}
    >
      <svg className="floating-cart-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
      </svg>
      {count > 0 && (
        <span className="floating-cart-badge" aria-label={`${count} item di keranjang`}>
          {count > 9 ? '9+' : count}
        </span>
      )}
    </button>
  )
}